import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, MapPin, Globe, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Reveal, Eyebrow, MaskedLine } from '../components/Reveal';
import api from '../lib/api';
import { SERVICES, ORG } from '../data/content';

const initial = {
  full_name: '', organisation: '', email: '', phone: '', service: '', location: '', description: '', consent: false, website: '',
};

export default function Contact() {
  const [params] = useSearchParams();
  const preselect = SERVICES.find((s) => s.slug === params.get('service'))?.title || '';
  const [form, setForm] = useState({ ...initial, service: preselect });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = 'Please enter your full name';
    if (form.organisation.trim().length < 2) er.organisation = 'Please enter your organisation';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = 'Please enter a valid business email';
    if (!form.service) er.service = 'Please select a service of interest';
    if (form.description.trim().length < 20) er.description = 'Please describe your project (at least 20 characters)';
    if (!form.consent) er.consent = 'Consent is required to submit';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post('/contact', form);
      setDone(true);
      toast.success('Thank you — your enquiry has been received.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Submission failed. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (k) =>
    `w-full rounded-xl border bg-white px-4 py-3.5 text-sm text-brand-black outline-none transition-colors placeholder:text-brand-body/50 focus:border-brand-green ${
      errors[k] ? 'border-red-400' : 'border-brand-grey'
    }`;

  return (
    <>
      <SEO
        title="Contact | INSIGHTS STRATEGY — Let's Start a Conversation"
        description="Tell INSIGHTS STRATEGY about your challenge, research requirement or strategic priority. A Division of Kalpataru Enterprise, Kolkata, India. info@insightsstrategy.in"
        path="/contact"
      />
      <section className="relative overflow-hidden bg-brand-deep" data-testid="contact-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(23,107,58,0.16),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-44 lg:px-8 lg:pb-24">
          <Eyebrow dark>Contact</Eyebrow>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="contact-headline">
            <MaskedLine delay={0.15}>Let's Start a</MaskedLine>
            <MaskedLine delay={0.3}><span className="text-brand-green">Conversation.</span></MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
              Tell us about your challenge, research requirement, or strategic priority. We would welcome the opportunity to explore how we can support you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-offwhite">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-12 lg:px-8 lg:py-28">
          <Reveal className="lg:col-span-4">
            <div className="rounded-3xl bg-brand-deep p-8 text-white sm:p-10" data-testid="contact-details-card">
              <h2 className="font-heading text-xl font-bold">{ORG.name}</h2>
              <p className="mt-1 text-xs font-medium uppercase tracking-[0.18em] text-white/50">{ORG.division}</p>
              <ul className="mt-8 space-y-6 text-sm">
                <li className="flex items-start gap-4">
                  <Mail size={18} className="mt-0.5 shrink-0 text-brand-green" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-white/45">Email</p>
                    <a href={`mailto:${ORG.email}`} className="mt-1 block transition-colors hover:text-brand-green" data-testid="contact-email-link">{ORG.email}</a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Globe size={18} className="mt-0.5 shrink-0 text-brand-green" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-white/45">Website</p>
                    <p className="mt-1">{ORG.website}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={18} className="mt-0.5 shrink-0 text-brand-green" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-white/45">Location</p>
                    <p className="mt-1">{ORG.location}</p>
                  </div>
                </li>
              </ul>
              <div className="mt-10 border-t border-white/10 pt-8">
                <p className="text-xs leading-relaxed text-white/45">We typically respond to enquiries within two working days.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8">
            <div className="rounded-3xl border border-brand-grey bg-white p-8 sm:p-10" data-testid="contact-form-card">
              {done ? (
                <div className="flex flex-col items-start gap-4 py-10" data-testid="contact-success">
                  <CheckCircle2 size={44} className="text-brand-green" />
                  <h2 className="font-heading text-2xl font-bold text-brand-black">Thank you for reaching out.</h2>
                  <p className="max-w-md text-sm leading-relaxed text-brand-body">
                    Your enquiry has been received and shared with our team. We will respond to you at <span className="font-semibold text-brand-black">{form.email}</span> shortly.
                  </p>
                  <button
                    onClick={() => { setForm(initial); setDone(false); }}
                    data-testid="contact-send-another"
                    className="mt-2 font-heading text-sm font-bold text-brand-deepgreen"
                  >
                    Send another enquiry →
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="full_name" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-brand-black">Full Name *</label>
                      <input id="full_name" data-testid="contact-input-name" className={inputCls('full_name')} value={form.full_name} onChange={set('full_name')} placeholder="Your name" />
                      {errors.full_name && <p className="mt-1.5 text-xs text-red-500" data-testid="error-name">{errors.full_name}</p>}
                    </div>
                    <div>
                      <label htmlFor="organisation" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-brand-black">Organisation *</label>
                      <input id="organisation" data-testid="contact-input-organisation" className={inputCls('organisation')} value={form.organisation} onChange={set('organisation')} placeholder="Company or institution" />
                      {errors.organisation && <p className="mt-1.5 text-xs text-red-500" data-testid="error-organisation">{errors.organisation}</p>}
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-brand-black">Business Email *</label>
                      <input id="email" type="email" data-testid="contact-input-email" className={inputCls('email')} value={form.email} onChange={set('email')} placeholder="name@organisation.com" />
                      {errors.email && <p className="mt-1.5 text-xs text-red-500" data-testid="error-email">{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="phone" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-brand-black">Phone Number <span className="text-brand-body/60 normal-case">(optional)</span></label>
                      <input id="phone" data-testid="contact-input-phone" className={inputCls('phone')} value={form.phone} onChange={set('phone')} placeholder="+91 …" />
                    </div>
                    <div>
                      <label htmlFor="service" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-brand-black">Service of Interest *</label>
                      <select id="service" data-testid="contact-select-service" className={inputCls('service')} value={form.service} onChange={set('service')}>
                        <option value="">Select a capability…</option>
                        {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                        <option value="Other / Not sure yet">Other / Not sure yet</option>
                      </select>
                      {errors.service && <p className="mt-1.5 text-xs text-red-500" data-testid="error-service">{errors.service}</p>}
                    </div>
                    <div>
                      <label htmlFor="location" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-brand-black">Project Location</label>
                      <input id="location" data-testid="contact-input-location" className={inputCls('location')} value={form.location} onChange={set('location')} placeholder="City, state or region" />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="description" className="mb-2 block text-xs font-bold uppercase tracking-[0.12em] text-brand-black">Project Description *</label>
                      <textarea id="description" rows={5} data-testid="contact-input-description" className={`${inputCls('description')} resize-y`} value={form.description} onChange={set('description')} placeholder="Tell us about your objectives, context, timelines, or the questions you are trying to answer…" />
                      {errors.description && <p className="mt-1.5 text-xs text-red-500" data-testid="error-description">{errors.description}</p>}
                    </div>
                  </div>

                  <input type="text" name="website" value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

                  <label className="mt-6 flex cursor-pointer items-start gap-3" data-testid="contact-consent-label">
                    <input type="checkbox" checked={form.consent} onChange={set('consent')} data-testid="contact-input-consent" className="mt-1 h-4 w-4 shrink-0 accent-[#58A934]" />
                    <span className="text-xs leading-relaxed text-brand-body">
                      I consent to INSIGHTS STRATEGY (A Division of Kalpataru Enterprise) storing and processing the information provided to respond to my enquiry. *
                    </span>
                  </label>
                  {errors.consent && <p className="mt-1.5 text-xs text-red-500" data-testid="error-consent">{errors.consent}</p>}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-brand-green px-8 py-4 font-heading text-sm font-bold text-white transition-colors duration-300 hover:bg-brand-deepgreen disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? 'Sending…' : 'Submit Enquiry'}
                    {!submitting && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
